import React, { useEffect, useState } from "react";
import { Link, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, Mail, Wrench, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useViewAs } from "@/context/ViewAsContext";
// Sprint-V25 Issue L (Sa 12.09.2026) — HeroSection ersetzt durch
// 4-Varianten-Hero + Story + Offer + Sticky-Bottom-CTA.
// Sprint-V25 Issue L Hotfix (Sa 12.09.2026 spaet) — QuickTestSection
// bleibt als inline 5-Fragen-Selbsttest BETWEEN Offer und Registrierung
// (Andreas-Direktive „erst der Test, dann registrieren").
import HeroHookSection from "@/components/landing/HeroHookSection";
import StorySection from "@/components/landing/StorySection";
import OfferSection from "@/components/landing/OfferSection";
import StickyBottomCTA from "@/components/landing/StickyBottomCTA";
import { QuickTestSection } from "@/components/landing/QuickTestSection";
import useHookVariant from "@/hooks/useHookVariant";
import { trackLandingEvent } from "@/lib/landingAnalytics";
import { trackResultsCtaClick } from "@/lib/ga4";
import { KlickTippOptin } from "@/components/KlickTippOptin";
import LoginModal from "@/components/LoginModal";
import { BRAND } from "@/config/brand";

// ------------------------------- Top-Bar -------------------------------
// v2.6 §1.5 + v2.6.2 Hotfix D — Logo links, "Anmelden"/"Dashboard" Toggle rechts,
// Dev-Login-Button NUR wenn /api/config liefert test_login_enabled=true.
// Sprint 6 HOCH 4 — „Anmelden" öffnet jetzt Modal-Dialog (statt /login-Redirect).
function TopBar({ isAuthenticated, isPionier }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [testLoginEnabled, setTestLoginEnabled] = useState(false);
  const [devLoading, setDevLoading] = useState(false);
  const [devError, setDevError] = useState("");
  // Sprint 7 (Slim) — zwei separate Modal-States für Login vs. Registrierung.
  const [loginOpen, setLoginOpen] = useState(false);
  const [optinOpen, setOptinOpen] = useState(false);
  // Vorlaunch 07.08.2026 — Pre-Launch-Modus schaltet oeffentliche CTAs um:
  //   „Registrieren" → „Pionier werden" (Warteliste-Opt-in)
  //   „Anmelden"     → „Bereits Pionier? Anmelden"
  const [prelaunchActive, setPrelaunchActive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/config").then(
      ({ data }) => {
        if (!cancelled) setTestLoginEnabled(!!data?.test_login_enabled);
      },
      () => { /* swallow — Endpoint optional */ },
    );
    api.get("/prelaunch/status").then(
      ({ data }) => {
        if (!cancelled) setPrelaunchActive(!!data?.prelaunch);
      },
      () => { /* swallow */ },
    );
    return () => { cancelled = true; };
  }, []);

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleDevLogin = async (tier) => {
    setDevError("");
    setDevLoading(true);
    setDropdownOpen(false);
    try {
      const url = tier === "free" ? "/auth/test/login?license_tier=free" : "/auth/test/login";
      await api.post(url, { username: "Test", password: "54321" });
      navigate("/cockpit", { replace: true });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        setDevError("Zu viele Versuche. 5 Min warten.");
      } else if (status === 404) {
        setDevError("In dieser Umgebung deaktiviert.");
      } else {
        setDevError(formatApiErrorDetail(e?.response?.data?.detail) || "Login fehlgeschlagen.");
      }
      setDevLoading(false);
    }
  };

  return (
    <header
      className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-slate-100"
      data-testid="lp-topbar"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-2 sm:gap-3">
        <Link to="/" className="flex items-center shrink-0 min-w-0" data-testid="lp-logo">
          <img
            src="/brand/flex4success-wordmark.png"
            alt={BRAND.name}
            data-testid="lp-brand-wordmark"
            className="h-7 sm:h-10 w-auto select-none"
            draggable="false"
          />
        </Link>
        <div className="flex items-center gap-3 sm:gap-4">
          {isPionier ? (
            <>
              <Link
                to="/dashboard/pionier"
                data-testid="lp-topbar-cockpit"
                className="text-sm font-bold text-white bg-[#4563BB] hover:bg-[#3a54a3] rounded-lg px-3 py-1.5 sm:px-4 sm:py-2 transition-colors"
              >
                Mein PocketCoach
              </Link>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await logout();
                  } catch (_e) {
                    /* Logout-Fehler ignorieren, User bekommt kein negatives Feedback */
                  }
                }}
                data-testid="lp-topbar-logout"
                className="text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors"
              >
                Abmelden
              </button>
            </>
          ) : isAuthenticated ? (
            <Link
              to="/cockpit"
              data-testid="lp-topbar-dashboard"
              className="text-sm font-semibold text-[var(--fts-primary-deep)] hover:text-[var(--fts-primary-bright)]"
            >
              PocketCoach →
            </Link>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setLoginOpen(true)}
                data-testid="lp-topbar-login"
                className="text-sm font-semibold text-[var(--fts-primary-deep)] hover:text-[var(--fts-primary-bright)]"
              >
                {"Anmelden"}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (prelaunchActive) {
                    navigate("/warteliste");
                  } else {
                    setOptinOpen(true);
                  }
                }}
                data-testid="lp-topbar-register"
                className="text-sm font-bold text-white bg-[var(--fts-accent)] hover:opacity-90 rounded-lg px-3 py-1.5 sm:px-4 sm:py-2 transition-opacity"
              >
                {"Registrieren"}
              </button>
            </>
          )}
          {testLoginEnabled && (
            <div className="relative hidden sm:block" data-testid="lp-topbar-dev-dropdown">
              <button
                type="button"
                onClick={() => setDropdownOpen((v) => !v)}
                disabled={devLoading}
                data-testid="lp-topbar-dev-login"
                title={devError || "Dev-Schnelllogin als Test-User"}
                className="inline-flex items-center gap-1.5 text-xs text-[var(--fts-text-muted)] hover:text-[var(--fts-primary-deep)] border border-dashed border-[var(--fts-neutral-200)] hover:border-[var(--fts-primary-bright)] rounded-md px-2.5 py-1 disabled:opacity-50"
              >
                {devLoading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Wrench className="w-3 h-3" />
                )}
                <span className="hidden sm:inline">Test-User</span>
                <span aria-hidden="true">{dropdownOpen ? "▴" : "▾"}</span>
              </button>
              {dropdownOpen && (
                <div
                  role="menu"
                  data-testid="lp-topbar-dev-menu"
                  className="absolute right-0 mt-2 w-56 rounded-lg border border-[var(--fts-neutral-200)] bg-white shadow-lg overflow-hidden z-40"
                >
                  <button
                    type="button"
                    onClick={() => handleDevLogin("pro_solo_monthly")}
                    data-testid="lp-topbar-dev-pro"
                    className="block w-full text-left px-4 py-2.5 text-xs hover:bg-[var(--fts-neutral-50)]"
                  >
                    <span className="font-bold text-[var(--fts-text-primary)]">Pro Solo Test-User</span>
                    <span className="block text-[10px] text-[var(--fts-text-muted)]">alle 4 Fokustypen sichtbar</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDevLogin("free")}
                    data-testid="lp-topbar-dev-free"
                    className="block w-full text-left px-4 py-2.5 text-xs hover:bg-[var(--fts-neutral-50)] border-t border-[var(--fts-neutral-200)]"
                  >
                    <span className="font-bold text-[var(--fts-text-primary)]">Free Test-User</span>
                    <span className="block text-[10px] text-[var(--fts-text-muted)]">1 Fokustyp voll, 3 Preview</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {devError && (
        <p
          data-testid="lp-topbar-dev-error"
          className="max-w-5xl mx-auto px-6 lg:px-8 text-xs text-red-600 pb-2 -mt-1 text-right"
        >
          {devError}
        </p>
      )}
      {/* Sprint 7 (Slim) — Login + Opt-In als separate Modale */}
      <LoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        mode="login"
      />
      <LoginModal
        open={optinOpen}
        onClose={() => setOptinOpen(false)}
        mode="optin"
      />
    </header>
  );
}

// ---------------------- Referral-Personalisierung (V38) --------
function ReferralBanner() {
  const [ref, setRef] = React.useState(null);
  React.useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const code = (url.searchParams.get("ref") || "").trim().toUpperCase();
      if (code && /^[A-Z0-9]{4,24}$/.test(code)) setRef(code);
      else {
        // Fallback: from localStorage (App.js schreibt es beim ersten Landing-Hit)
        const raw = localStorage.getItem("f4s_ref_code");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.code && Date.now() - Number(parsed.ts || 0) < (parsed.ttl || 0)) {
            setRef(parsed.code);
          }
        }
      }
    } catch { /* silent */ }
  }, []);
  if (!ref) return null;
  return (
    <div
      className="border-b border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10"
      data-testid="lp-referral-banner"
      data-ref-code={ref}
    >
      <div className="max-w-4xl mx-auto px-4 py-2.5 text-center text-sm text-amber-900 dark:text-amber-200">
        <span className="font-semibold">Jemand hat dir das geschickt.</span>{" "}
        Der Fokustyp-Test ist kostenlos — 5 Fragen, 2 Minuten, ein Aha-Moment.
      </div>
    </div>
  );
}

// ---------------------- Footer (minimal) -----------------------
function MinimalFooter() {  const { isDevMode } = useViewAs();
  const [build, setBuild] = useState(null);

  useEffect(() => {
    if (!isDevMode) return;
    fetch("/build_info.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setBuild(d))
      .catch(() => setBuild(null));
  }, [isDevMode]);

  const versionLabel = isDevMode && build
    ? `${build.version} · build ${build.build} · ${build.built_at}`
    : "v2.10.1";

  return (
    <footer
      className="border-t border-[var(--fts-neutral-200)] py-6 px-6 lg:px-8"
      data-testid="lp-footer"
    >
      <div className="max-w-5xl mx-auto mb-8" data-testid="lp-footer-newsletter">
        {/* B.1 Sprint 4c — Newsletter-Box auf Landing entfernt */}
      </div>
      <div className="max-w-5xl mx-auto flex flex-wrap gap-x-6 gap-y-2 items-center justify-center text-xs text-[var(--fts-text-muted)]">
        <a
          href="https://effektiv-kommunizieren.de/impressum"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-[var(--fts-primary-deep)]"
          data-testid="lp-footer-impressum"
        >
          Impressum
        </a>
        <a
          href="https://effektiv-kommunizieren.de/datenschutz"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-[var(--fts-primary-deep)]"
          data-testid="lp-footer-datenschutz"
        >
          Datenschutz
        </a>
        <a
          href="mailto:hello@aj-kommunikation.de"
          className="hover:text-[var(--fts-primary-deep)]"
          data-testid="lp-footer-kontakt"
        >
          Kontakt
        </a>
        <span
          data-testid="lp-footer-brand-name"
          className="font-semibold tracking-[0.04em] text-[var(--fts-text-muted)]"
        >
          {BRAND.name}
        </span>
        <span aria-hidden="true" className="text-[var(--fts-neutral-200)] select-none">·</span>
        <span
          data-testid="lp-footer-brand"
          className="brand-marker font-extrabold tracking-[0.08em] text-[var(--fts-primary-deep)] uppercase"
        >
          Fokustypen Code
        </span>
        <span>© 2026 by Andreas Jung</span>
      </div>
      <div
        className="mt-3 text-center text-[10px] text-slate-400 tracking-wide"
        data-testid="footer-version"
      >
        {versionLabel}
      </div>
    </footer>
  );
}

// ---------------------- Magic-Link-Modal -----------------------
function MagicLinkModal({ open, onClose, source, fokustyp }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    setError("");
    try {
      // Sprint V95 (Di 16.09.2026, Andreas-Direktive) — Bug-Fix:
      // Fokustyp aus QuickTest wurde bisher hier verworfen. Jetzt bis
      // zum Backend durchgereicht → Dashboard zeigt echten Fokustyp.
      const body = { email, source };
      if (fokustyp) body.fokustyp = fokustyp;
      // Sprint V97 (Mi 16.09.2026, Todd/Andreas) — Session-Kontext fuer
      // Meta CAPI PageView-Enrichment (Match-Quality-Boost).
      try {
        const sid = localStorage.getItem("f4s_session_id");
        if (sid) body.session_id = sid;
        if (typeof window !== "undefined" && window.location?.href) {
          body.original_page_url = window.location.href;
        }
      } catch { /* silent */ }
      await api.post("/auth/magic/request", body);
      setSent(true);
    } catch (err) {
      setError(
        formatApiErrorDetail(err) ||
          "Etwas ist schiefgegangen. Bitte erneut versuchen.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="lp-magic-modal"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-7 relative">
        <button
          type="button"
          onClick={onClose}
          data-testid="lp-magic-modal-close"
          className="absolute top-3 right-3 w-9 h-9 inline-flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"
        >
          <X className="w-4 h-4" />
        </button>
        {sent ? (
          <div className="text-center py-2" data-testid="lp-magic-modal-sent">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <Mail className="w-6 h-6 text-[var(--fts-success)]" />
            </div>
            <h3 className="mt-4 text-xl font-bold text-[var(--fts-text-primary)]">
              Check dein Postfach.
            </h3>
            <p className="mt-2 text-sm text-[var(--fts-text-muted)]">
              Ein Magic-Link ist auf dem Weg zu{" "}
              <span className="font-semibold">{email}</span>.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 inline-flex items-center gap-2 rounded-xl border border-[var(--fts-neutral-200)] hover:border-[var(--fts-primary-bright)] text-[var(--fts-text-primary)] font-semibold px-5 py-2.5 text-sm"
            >
              Schließen
            </button>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-bold text-[var(--fts-text-primary)]">
              Sichere dir dein Ergebnis per E-Mail
            </h3>
            <p className="mt-2 text-sm text-[var(--fts-text-muted)]">
              Kein Passwort. Du bekommst sofort einen Magic-Link in dein Postfach.
            </p>
            <form onSubmit={handleSubmit} className="mt-5 space-y-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="deine.email@beispiel.de"
                data-testid="lp-magic-modal-email"
                className="w-full rounded-xl border border-[var(--fts-neutral-200)] focus:border-[var(--fts-primary-bright)] outline-none px-4 py-3 text-sm"
              />
              {error && (
                <p
                  className="text-sm text-red-600"
                  data-testid="lp-magic-modal-error"
                >
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                data-testid="lp-magic-modal-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--fts-accent)] hover:opacity-90 text-white font-bold px-5 py-3 text-sm disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Kostenfrei testen <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
              <p className="text-xs text-[var(--fts-text-muted)] text-center leading-relaxed">
                Mit Klick akzeptierst du unsere{" "}
                <a
                  href="https://effektiv-kommunizieren.de/datenschutz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-[var(--fts-primary-deep)]"
                >
                  Datenschutzrichtlinien
                </a>
                .
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------- Main LP Container -----------------------
// Master-Prompt v2.6.4 — Radikale Reduktion: Hero ohne Button, Test-Section mit
// Spiegel-Headline + 5 Fragen + Ergebnis inline, CTA "kostenfrei testen" am Ende,
// KEINE Pain/Motivation/Trust-Sections, KEINE Pricing-Info auf der LP.
export default function LandingPage() {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const isPionier = isAuthenticated && (user.is_pionier === true);
  const [magicOpen, setMagicOpen] = useState(false);
  const [magicSource, setMagicSource] = useState("lp_test_completed");
  const [magicFokustyp, setMagicFokustyp] = useState(null);
  // Sprint-V25 Issue L Hotfix (Sa 12.09.2026 09:25) — Andreas-Direktive:
  // „Als aktiver User muss ich mich hier auch anmelden koennen" — sekundaerer
  // Login-Modal (mode="login", Magic-Link nur fuer bereits registrierte User).
  const [loginOpen, setLoginOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Hotfix Auth-Consolidation (10.02.2026) — Deep-Link-Support für den
  // Magic-Link-Opt-in-Flow. /register wird via Fix 2a auf
  // /?open=magic-optin&fokustyp=…&source=… umgeleitet. Hier greifen wir das
  // ab und öffnen das LoginModal automatisch. Query-Parameter werden nach
  // dem Auto-Open weggeräumt, damit ein späterer Modal-Close keinen
  // Endlos-Reopen triggert.
  const VALID_FT = ["eule", "biene", "katze", "hund"];
  useEffect(() => {
    const flag = searchParams.get("open");
    if (flag === "magic-optin") {
      const ft = (searchParams.get("fokustyp") || "").toLowerCase().trim();
      const src = (searchParams.get("source") || "register_redirect").trim();
      setMagicSource(src || "register_redirect");
      setMagicFokustyp(VALID_FT.includes(ft) ? ft : null);
      setMagicOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      next.delete("fokustyp");
      next.delete("source");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sprint-V25 Issue L — A/B-Hook-Variante + Landing-Tracking.
  // Hooks MUESSEN vor jedem conditional return stehen (React Rules of Hooks).
  // Variante wird beim ersten Render bestimmt (Query > Cookie > Random)
  // und bleibt stabil, damit hero_view und cta_click derselben Variante
  // zugerechnet werden.
  const hookVariant = useHookVariant();
  const isAnon = !(user && typeof user === "object");
  useEffect(() => {
    if (!hookVariant || !isAnon) return;
    trackLandingEvent({ event: "hero_view", hookVariant });
  }, [hookVariant, isAnon]);

  // Hotfix v14d — Eingeloggte User landen direkt im Cockpit (Sprint-8-Default).
  // Sprint 12+I5 (04.07.2026) — Pioniere landen in ihrem eigenen Cockpit
  // (/dashboard/pionier), damit sie in ihrer Pionier-Bubble bleiben und
  // nicht durch die Free-User-Landing-Page laufen.
  //
  // Post-Launch-H5 (10.02.2026) — PIONIER-Switch-Konsistenz (Boardroom-CoS):
  // Wenn Env-Flag PIONIER_ACTIVE=false, sollen Pioniere in die Standard-Free-UI
  // migrieren — inklusive der LandingPage-Weiche. Analog zu Cockpit.jsx.
  if (user && typeof user === "object") {
    if (user.is_pionier && user.pionier_active === true) {
      return <Navigate to="/dashboard/pionier" replace />;
    }
    return <Navigate to="/cockpit" replace />;
  }

  const openMagic = (source, fokustyp = null) => {
    setMagicSource(source);
    setMagicFokustyp(fokustyp);
    setMagicOpen(true);
  };

  // Sprint-V25 Issue L Hotfix (Sa 12.09.2026 spaet) — Andreas-Direktive:
  // „Test starten"-CTAs sollen den User NICHT direkt in die Registrierung
  // schicken, sondern zur inline QuickTestSection scrollen. Der Modal wird
  // erst nach dem Test-Ergebnis geoeffnet (QuickTestSection.onMagicLink).
  const scrollToQuickTest = () => {
    if (typeof document === "undefined") return;
    const el = document.getElementById("lp-quicktest");
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleLandingCta = (section) => {
    trackLandingEvent({ event: "cta_click", hookVariant, section });
    scrollToQuickTest();
  };

  // Wird von QuickTestSection nach dem Ergebnis-Screen ausgeloest, wenn der
  // User „Jetzt registrieren" klickt. Feuert test_complete (mit Fokustyp),
  // dann oeffnet der Magic-Link-Modal.
  // Sprint V102.ab (02.10.2026) — question_set aus Cookie mitlesen und
  // ins Analytics-Event + ins Source-String kodieren, damit das Admin-
  // Dashboard die optin_rate v1 vs. v2 berechnen kann.
  //
  // Sprint V103.d (06.10.2026, GA4-Opt-in-Lueckenschluss) — Hier ist die
  // gemeinsame Verarbeitungsstelle fuer den Opt-in-Klick auf beiden Wegen
  // (mit und ohne Video-Bruecke). Das neue GA4-Ereignis `results_cta_click`
  // feuert genau einmal — unabhaengig von V99-Variante. Vorher war
  // Schritt 4 des Trichters halb blind, weil nur `video_bridge_cta_click`
  // (nur Variante A) gezaehlt wurde.
  const handleQuickTestMagicLink = (source, fokustyp) => {
    let questionSet = null;
    try {
      const match = (document.cookie || "").match(/f4s_question_set=([^;]+)/);
      if (match) questionSet = decodeURIComponent(match[1]);
    } catch (_e) { /* noop */ }
    // Legacy: Backend-persistierter Opt-in-Klick-Zaehler (`opt_in_clicked`
    // in `landing_events`). Bleibt wie bisher, damit die Vergangenheit
    // vergleichbar bleibt.
    trackLandingEvent({
      event: "test_complete",
      hookVariant,
      fokustypResult: fokustyp || null,
      questionSet,
    });
    // Neu: GA4-Browser-Event `results_cta_click`. Deckt beide V99-Varianten
    // additiv ab. Siehe `lib/ga4.js::trackResultsCtaClick` fuer Details.
    trackResultsCtaClick({
      fokustyp: fokustyp || null,
      questionSet,
      hookVariant,
      source: source || null,
    });
    const srcWithSet = questionSet
      ? `${source || `landing_quicktest_hook_${hookVariant}`}_qs${questionSet}`
      : (source || `landing_quicktest_hook_${hookVariant}`);
    openMagic(srcWithSet, fokustyp);
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-white dark:bg-slate-950"
      data-testid="lp-page"
      data-hook-variant={hookVariant}
    >
      <TopBar isAuthenticated={isAuthenticated} isPionier={isPionier} />
      <main className="flex-1">
        {/* Sprint V38 (So 15.09.2026) — Referral-Personalisierung:
            wenn ?ref=CODE in URL, kleiner Hinweis oben, dass jemand die
            Seite geteilt hat. Baut Vertrauen bevor der User scrollt. */}
        <ReferralBanner />
        <HeroHookSection
          variant={hookVariant}
          onCtaClick={handleLandingCta}
          onLoginClick={() => setLoginOpen(true)}
        />
        <StorySection onCtaClick={handleLandingCta} />
        <OfferSection
          onCtaClick={handleLandingCta}
          onLoginClick={() => setLoginOpen(true)}
        />
        {/* Sprint-V25 Issue L Hotfix (Sa 12.09.2026 spaet) — inline 5-Fragen-
            Selbsttest. CTA-Klick auf Hero/Story/Offer/Sticky scrollt hierhin. */}
        <div id="lp-quicktest">
          <QuickTestSection onMagicLink={handleQuickTestMagicLink} />
        </div>
      </main>
      <StickyBottomCTA onCtaClick={handleLandingCta} />
      <MinimalFooter />
      <LoginModal
        key={magicOpen ? `open-${magicSource}-${magicFokustyp || "none"}` : "closed"}
        open={magicOpen}
        onClose={() => setMagicOpen(false)}
        mode="optin"
        source={magicSource}
        fokustyp={magicFokustyp}
        onMagicRequested={() => trackLandingEvent({ event: "test_start", hookVariant })}
      />
      {/* Sprint-V25 Issue L Hotfix (Sa 12.09.2026 09:25) — Sekundaerer
          Login-Modal fuer rueckkehrende User (mode="login"). */}
      <LoginModal
        key={loginOpen ? "login-open" : "login-closed"}
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        mode="login"
        source="lp_returning_user"
      />
    </div>
  );
}
